import React from "react";
import Icon from "components/AppIcon";
import { APP_TOAST_EVENT } from "utils/toast";

const EXIT_MS = 260;

const TOAST_VARIANTS = {
  info: {
    label: "Info",
    icon: "Info",
    borderColor: "rgba(96, 165, 250, 0.42)",
    iconBackground: "rgba(59, 130, 246, 0.22)",
    iconColor: "#3B82F6",
    background: "radial-gradient(circle at 14% 18%, rgba(96, 165, 250, 0.22), transparent 42%), linear-gradient(180deg, rgba(8, 23, 44, 0.98), rgba(7, 18, 35, 0.98))",
    shadow: "0 22px 44px rgba(8, 23, 44, 0.46)",
    textColor: "#EFF6FF",
    labelColor: "rgba(191, 219, 254, 0.96)",
    closeColor: "rgba(219, 234, 254, 0.92)",
    trackColor: "rgba(30, 64, 175, 0.42)",
    progress: "#3B82F6",
  },
  warning: {
    label: "Aviso",
    icon: "ShieldAlert",
    borderColor: "rgba(251, 191, 36, 0.42)",
    iconBackground: "rgba(245, 158, 11, 0.24)",
    iconColor: "#F59E0B",
    background: "radial-gradient(circle at 14% 18%, rgba(251, 191, 36, 0.22), transparent 42%), linear-gradient(180deg, rgba(47, 28, 6, 0.98), rgba(34, 21, 6, 0.98))",
    shadow: "0 22px 44px rgba(47, 28, 6, 0.44)",
    textColor: "#FFFBEB",
    labelColor: "rgba(253, 230, 138, 0.96)",
    closeColor: "rgba(254, 243, 199, 0.92)",
    trackColor: "rgba(146, 64, 14, 0.44)",
    progress: "#F59E0B",
  },
  error: {
    label: "Erro",
    icon: "AlertTriangle",
    borderColor: "rgba(248, 113, 113, 0.44)",
    iconBackground: "rgba(239, 68, 68, 0.24)",
    iconColor: "#EF4444",
    background: "radial-gradient(circle at 14% 18%, rgba(248, 113, 113, 0.22), transparent 42%), linear-gradient(180deg, rgba(52, 12, 12, 0.98), rgba(34, 10, 10, 0.98))",
    shadow: "0 22px 44px rgba(52, 12, 12, 0.44)",
    textColor: "#FEF2F2",
    labelColor: "rgba(254, 202, 202, 0.96)",
    closeColor: "rgba(254, 226, 226, 0.92)",
    trackColor: "rgba(153, 27, 27, 0.44)",
    progress: "#EF4444",
  },
  success: {
    label: "Sucesso",
    icon: "BadgeCheck",
    borderColor: "rgba(52, 211, 153, 0.4)",
    iconBackground: "rgba(16, 185, 129, 0.24)",
    iconColor: "#10B981",
    background: "radial-gradient(circle at 14% 18%, rgba(52, 211, 153, 0.2), transparent 42%), linear-gradient(180deg, rgba(8, 43, 33, 0.98), rgba(7, 30, 23, 0.98))",
    shadow: "0 22px 44px rgba(8, 43, 33, 0.44)",
    textColor: "#ECFDF5",
    labelColor: "rgba(167, 243, 208, 0.96)",
    closeColor: "rgba(209, 250, 229, 0.92)",
    trackColor: "rgba(6, 95, 70, 0.44)",
    progress: "#10B981",
  },
};

function ToastCard({ item, onDismiss }) {
  const variant = TOAST_VARIANTS?.[item?.variant] || TOAST_VARIANTS.info;

  React.useEffect(() => {
    if (!item?.duration || item?.duration <= 0) return undefined;

    const timer = window.setTimeout(() => {
      onDismiss(item?.id);
    }, item.duration);

    return () => window.clearTimeout(timer);
  }, [item?.duration, item?.id, onDismiss]);

  return (
    <div
      className={`app-toast ${item?.exiting ? "app-toast-exit" : "app-toast-enter"}`}
      style={{
        "--app-toast-text": variant.textColor,
        "--app-toast-label": variant.labelColor,
        "--app-toast-close": variant.closeColor,
        "--app-toast-track": variant.trackColor,
        borderColor: variant.borderColor,
        background: variant.background,
        boxShadow: variant.shadow,
      }}
    >
      <div className="app-toast-body">
        <div className="app-toast-icon" style={{ background: variant.iconBackground }}>
          <Icon name={variant.icon} size={16} color={variant.iconColor} />
        </div>
        <div className="app-toast-copy">
          <div className="app-toast-label">{item?.title || variant.label}</div>
          <div className="app-toast-message">{item?.message}</div>
        </div>
        <button
          type="button"
          onClick={() => onDismiss(item?.id)}
          className="app-toast-close"
          aria-label="Fechar notificação"
        >
          <Icon name="X" size={14} color="currentColor" />
        </button>
      </div>
      {item?.duration > 0 && (
        <div className="app-toast-progress-track">
          <span
            className="app-toast-progress-bar"
            style={{
              background: variant.progress,
              animationDuration: `${item.duration}ms`,
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function ToastViewport() {
  const [items, setItems] = React.useState([]);

  const dismissById = React.useCallback((id) => {
    setItems((current) => {
      const target = current?.find((item) => item?.id === id);
      if (!target || target?.exiting) return current;

      window.setTimeout(() => {
        setItems((next) => next?.filter((item) => item?.id !== id));
      }, EXIT_MS);

      return current?.map((item) => (item?.id === id ? { ...item, exiting: true } : item));
    });
  }, []);

  React.useEffect(() => {
    const handleToastEvent = (event) => {
      const detail = event?.detail;

      if (detail?.type === "push" && detail?.toast) {
        setItems((current) => [...current, { ...detail.toast, exiting: false }]);
        return;
      }

      if (detail?.type === "dismiss" && detail?.id) {
        dismissById(detail.id);
        return;
      }

      if (detail?.type === "clear") {
        setItems([]);
      }
    };

    window.addEventListener(APP_TOAST_EVENT, handleToastEvent);
    return () => window.removeEventListener(APP_TOAST_EVENT, handleToastEvent);
  }, [dismissById]);

  if (!items?.length) return null;

  return (
    <div className="app-toast-viewport" aria-live="polite" aria-atomic="false">
      {items?.map((item) => (
        <ToastCard key={item?.id} item={item} onDismiss={dismissById} />
      ))}
    </div>
  );
}